import { supabase } from "@/integrations/supabase/client";

/**
 * THE admin check, in the browser.
 *
 * ONE PREDICATE. `public.caller_is_superadmin()` reads `public.user_roles` and
 * nothing else. It replaced three answers to the same question: a column on
 * profiles, a grant row in user_roles, and a hardcoded uuid compared by hand in
 * ~97 places across 46 files.
 *
 * WHY THE HARDCODED UUID HAD TO GO. `user.id === "5ec92c56-…"` cannot grant
 * anybody anything. The day there is a second admin, every one of those checks
 * silently denies them, and the failure looks like a bug in the page rather than
 * a missing grant. A comparison against a constant is not access control; it is
 * a coincidence that happens to hold while there is exactly one admin.
 *
 * ADMIN IS NOT A TIER. A tier says what a user bought. This says what they may
 * administer. They were tangled together — 13 RLS policies gated admin on
 * `profiles.tier = 'superadmin'`, a value no row has ever held — and they are
 * now separate facts with separate homes.
 */

/** True when the signed-in user holds the superadmin grant. */
export async function isAdmin(): Promise<boolean> {
  const { data, error } = await supabase.rpc("caller_is_superadmin" as never);
  if (error) {
    // A failed check is not an admin. Logged loudly because a silently-denied
    // admin looks like a broken page, and that is how this took months to find.
    console.error("[isAdmin] caller_is_superadmin() failed; treating as not admin:", error.message);
    return false;
  }
  return data === true;
}
